import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Save, Loader2, Plus, Trash2, GripVertical, Eye, Shield, Lock, Zap, Smartphone, Scale, RefreshCw, BarChart3, Users, Headphones, FileText, Clock, CheckCircle, Star, Heart, Globe, Key, Wallet, TrendingUp, Award, Briefcase } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available icons for selection
const AVAILABLE_ICONS = [
  { name: 'Shield', icon: Shield, label: 'Shield' },
  { name: 'Lock', icon: Lock, label: 'Lock' },
  { name: 'Zap', icon: Zap, label: 'Lightning' },
  { name: 'Smartphone', icon: Smartphone, label: 'Smartphone' },
  { name: 'Scale', icon: Scale, label: 'Scale/Legal' },
  { name: 'RefreshCw', icon: RefreshCw, label: 'Refresh' },
  { name: 'BarChart3', icon: BarChart3, label: 'Chart' },
  { name: 'Users', icon: Users, label: 'Users' },
  { name: 'Headphones', icon: Headphones, label: 'Support' },
  { name: 'FileText', icon: FileText, label: 'Document' },
  { name: 'Clock', icon: Clock, label: 'Clock' },
  { name: 'CheckCircle', icon: CheckCircle, label: 'Check' },
  { name: 'Star', icon: Star, label: 'Star' },
  { name: 'Heart', icon: Heart, label: 'Heart' },
  { name: 'Globe', icon: Globe, label: 'Globe' },
  { name: 'Key', icon: Key, label: 'Key' },
  { name: 'Wallet', icon: Wallet, label: 'Wallet' },
  { name: 'TrendingUp', icon: TrendingUp, label: 'Trending' },
  { name: 'Award', icon: Award, label: 'Award' },
  { name: 'Briefcase', icon: Briefcase, label: 'Briefcase' },
];

// Icon component lookup
const ICON_MAP = {
  Shield, Lock, Zap, Smartphone, Scale, RefreshCw, BarChart3, Users, Headphones,
  FileText, Clock, CheckCircle, Star, Heart, Globe, Key, Wallet, TrendingUp, Award, Briefcase
};

// Default cards
const DEFAULT_CARDS = [
  { id: '1', icon: 'Shield', title: 'Secure Vault', description: 'Store all your important documents in one encrypted, protected space', enabled: true, order: 1 },
  { id: '2', icon: 'Zap', title: 'Quick Setup', description: 'Create your will in minutes with our guided step-by-step process', enabled: true, order: 2 },
  { id: '3', icon: 'Smartphone', title: 'Access Anywhere', description: 'View and manage your documents from any device, anytime', enabled: true, order: 3 },
  { id: '4', icon: 'Scale', title: 'Legally Valid', description: 'Documents crafted to meet all legal requirements and standards', enabled: true, order: 4 },
  { id: '5', icon: 'RefreshCw', title: 'Easy Updates', description: 'Modify your will anytime as your life circumstances change', enabled: true, order: 5 },
  { id: '6', icon: 'BarChart3', title: 'Track Progress', description: 'Monitor your will completion status with clear visual indicators', enabled: true, order: 6 },
  { id: '7', icon: 'Users', title: 'Trusted Contacts', description: 'Share access with family members for emergency situations', enabled: true, order: 7 },
  { id: '8', icon: 'Lock', title: 'Bank-Grade Security', description: 'Enterprise-level encryption protects your sensitive data', enabled: true, order: 8 },
  { id: '9', icon: 'Headphones', title: 'Expert Support', description: 'Get help from our team whenever you need assistance', enabled: true, order: 9 },
];

const LoginFeatures = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    section_title: "What You'll Get",
    cards: DEFAULT_CARDS
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/login-features`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings({
          enabled: data.enabled ?? true,
          section_title: data.section_title || "What You'll Get",
          cards: data.cards?.length > 0 ? data.cards : DEFAULT_CARDS
        });
      }
    } catch (error) {
      console.error('Error fetching login features:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/login-features`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Login features saved successfully');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const updateCard = (cardId, field, value) => {
    setSettings(prev => ({
      ...prev,
      cards: prev.cards.map(card => 
        card.id === cardId ? { ...card, [field]: value } : card
      )
    }));
  };

  const addCard = () => {
    const newId = String(Date.now());
    const newOrder = Math.max(...settings.cards.map(c => c.order), 0) + 1;
    setSettings(prev => ({
      ...prev,
      cards: [...prev.cards, {
        id: newId,
        icon: 'Star',
        title: 'New Feature',
        description: 'Enter description here',
        enabled: true,
        order: newOrder
      }]
    }));
  };

  const deleteCard = (cardId) => {
    if (settings.cards.length <= 1) {
      toast.error('At least one card is required');
      return;
    }
    setSettings(prev => ({
      ...prev,
      cards: prev.cards.filter(card => card.id !== cardId)
    }));
  };

  const moveCard = (cardId, direction) => {
    const cardIndex = settings.cards.findIndex(c => c.id === cardId);
    if (cardIndex === -1) return;
    
    const newIndex = direction === 'up' ? cardIndex - 1 : cardIndex + 1;
    if (newIndex < 0 || newIndex >= settings.cards.length) return;
    
    const newCards = [...settings.cards];
    [newCards[cardIndex], newCards[newIndex]] = [newCards[newIndex], newCards[cardIndex]];
    
    // Update order values
    newCards.forEach((card, index) => {
      card.order = index + 1;
    });
    
    setSettings(prev => ({ ...prev, cards: newCards }));
  };

  const resetToDefaults = () => {
    setSettings({
      enabled: true,
      section_title: "What You'll Get",
      cards: DEFAULT_CARDS
    });
    toast.info('Reset to default values');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  const enabledCards = settings.cards.filter(c => c.enabled).sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-6" data-testid="login-features-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white">Login Page Features</h1>
          <p className="text-slate-400 mt-1">Customize the &quot;What You&apos;ll Get&quot; section on client login page</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={resetToDefaults}
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            Reset to Defaults
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            data-testid="save-login-features-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Section Settings */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Section Settings</CardTitle>
          <CardDescription className="text-slate-400">
            Control visibility and title of the features section
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-white font-medium">Show Features Section</p>
                <p className="text-sm text-slate-400">Display the &quot;What You&apos;ll Get&quot; section on login page</p>
              </div>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
              data-testid="features-enabled-toggle"
            />
          </div>

          {settings.enabled && (
            <div className="space-y-2">
              <Label className="text-slate-300">Section Title</Label>
              <Input
                value={settings.section_title}
                onChange={(e) => setSettings(prev => ({ ...prev, section_title: e.target.value }))}
                placeholder="What You'll Get"
                className="bg-slate-800 border-slate-700 text-white max-w-md"
                data-testid="section-title-input"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Feature Cards */}
      {settings.enabled && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-white">Feature Cards</CardTitle>
                <CardDescription className="text-slate-400">
                  Customize each feature card&apos;s icon, title, and description
                </CardDescription>
              </div>
              <Button
                onClick={addCard}
                variant="outline"
                size="sm"
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Card
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {settings.cards.sort((a, b) => a.order - b.order).map((card, index) => {
              const IconComponent = ICON_MAP[card.icon] || Shield;
              return (
                <div
                  key={card.id}
                  className={`p-4 rounded-lg border ${card.enabled ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/30 border-slate-800 opacity-60'}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Drag Handle & Order */}
                    <div className="flex flex-col items-center gap-1 pt-2">
                      <GripVertical className="w-5 h-5 text-slate-600" />
                      <span className="text-xs text-slate-500">#{index + 1}</span>
                      <div className="flex flex-col gap-1 mt-2">
                        <button
                          onClick={() => moveCard(card.id, 'up')}
                          disabled={index === 0}
                          className="text-slate-500 hover:text-white disabled:opacity-30 text-xs"
                        >
                          ▲
                        </button>
                        <button
                          onClick={() => moveCard(card.id, 'down')}
                          disabled={index === settings.cards.length - 1}
                          className="text-slate-500 hover:text-white disabled:opacity-30 text-xs"
                        >
                          ▼
                        </button>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Icon Selector */}
                      <div className="space-y-2">
                        <Label className="text-slate-400 text-xs">Icon</Label>
                        <Select
                          value={card.icon}
                          onValueChange={(value) => updateCard(card.id, 'icon', value)}
                        >
                          <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                            <div className="flex items-center gap-2">
                              <IconComponent className="w-4 h-4 text-amber-400" />
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                            {AVAILABLE_ICONS.map((iconOption) => {
                              const Icon = iconOption.icon;
                              return (
                                <SelectItem
                                  key={iconOption.name}
                                  value={iconOption.name}
                                  className="text-white hover:bg-slate-700"
                                >
                                  <div className="flex items-center gap-2">
                                    <Icon className="w-4 h-4 text-amber-400" />
                                    {iconOption.label}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Title */}
                      <div className="space-y-2">
                        <Label className="text-slate-400 text-xs">Title</Label>
                        <Input
                          value={card.title}
                          onChange={(e) => updateCard(card.id, 'title', e.target.value)}
                          placeholder="Feature title"
                          maxLength={25}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>

                      {/* Description */}
                      <div className="space-y-2 lg:col-span-2">
                        <Label className="text-slate-400 text-xs">Description</Label>
                        <Textarea
                          value={card.description}
                          onChange={(e) => updateCard(card.id, 'description', e.target.value)}
                          placeholder="Brief description (2 lines max)"
                          maxLength={100}
                          rows={2}
                          className="bg-slate-800 border-slate-700 text-white resize-none"
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 pt-2">
                      <Switch
                        checked={card.enabled}
                        onCheckedChange={(checked) => updateCard(card.id, 'enabled', checked)}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteCard(card.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Live Preview */}
      {settings.enabled && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Live Preview</CardTitle>
            <CardDescription className="text-slate-400">
              Preview how the section will appear on the login page
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-950 rounded-xl p-8 border border-slate-800">
              {/* Section Header Preview */}
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
                  {settings.section_title || "What You'll Get"}
                </h2>
                <div className="w-12 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full"></div>
              </div>

              {/* Cards Preview Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {enabledCards.slice(0, 9).map((card) => {
                  const IconComponent = ICON_MAP[card.icon] || Shield;
                  return (
                    <div
                      key={card.id}
                      className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-amber-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold text-sm mb-1">
                            {card.title}
                          </h3>
                          <p className="text-slate-400 text-xs leading-relaxed line-clamp-2">
                            {card.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {enabledCards.length === 0 && (
                <p className="text-center text-slate-500">No enabled cards to preview</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LoginFeatures;
